import { useGate, useUnit } from 'effector-react';
import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material';
import { AutocompleteOptionWithHint } from '@shared/ui';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { useAutoSelectSingleOption } from '../../lib/use-auto-select-single-option';
import { ResidentAccountOwnerControlGate, residentAccountOwnerQuery } from './model';
import type { ResidentAccountOwner, Props } from './types';

export const ResidentAccountOwnerControl = withControl<
  Partial<AutocompleteProps<ResidentAccountOwner, boolean, boolean, boolean>> & Props
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    accountId,
    autoSelectSingleOption = false,
    excludeIds = [],
    ...props
  }) => {
    useGate(ResidentAccountOwnerControlGate, { accountId });
    const { data, pending } = useUnit(residentAccountOwnerQuery);

    const options = Array.isArray(data)
      ? data.filter(({ residentGuid }) => !excludeIds.includes(residentGuid))
      : [];

    const setValueFromControl = useAutoSelectSingleOption({
      enabled: autoSelectSingleOption && Boolean(accountId),
      loading: pending,
      multiple,
      options,
      setValue,
      value,
    });

    const handleChange = (
      _: unknown,
      newValue: string | ResidentAccountOwner | (string | ResidentAccountOwner)[] | null
    ) => getAutocompleteOnChange(newValue, setValueFromControl, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={options}
          multiple={multiple}
          loading={pending}
          disableClearable={!clearable}
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          renderOption={({ key, ...props }, option) => {
            const optionDescription = [option.phone, option.email]
              .filter(Boolean)
              .join(', ');

            return (
              <li key={key} {...props}>
                <AutocompleteOptionWithHint
                  label={option.label}
                  hint={optionDescription}
                />
              </li>
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
